import { addToCart } from '../cart';
import { KEYS } from '../constants/keys';
import {
  COLLECTION_SELECTED,
  DEFAULT,
  INFINITE,
  LOAD_MORE,
  PREV_PAGE,
  SELECTED_PRODUCT_ID,
  SELECTED_PRODUCT_PAGE,
} from '../constants/pagination';
import { getFilterSettings } from '../filter-tree';
import {
  handlePreorderAddToCart,
  handlePreOrderButtonState,
  isPreOrderVariant,
} from '../pre-order';
import { getCurrentCollection } from '../product-list';
import { getTemplate } from '../template';
import {
  buildProductDetailUrlWithVariant,
  debounce,
  formatCurrency,
  getQueryParamByKey,
  getSessionStorage,
  getThemeSettings,
  getUrlScheme,
  safeParseJSON,
  setQueryParamHistory,
  setSessionStorage,
  setWindowLocation,
} from '../utils';
import { translateWithComponent } from '../utils/translation';
import { focusFirstElement, trapFocus } from '../utils/wcag-modal';

export * from './product-swatch';

let prevSelectedPopUp = null;
window.focusedElementBeforeMiniCart = null;

/**
 * @type {import('./index.d').quickAddToCart}
 */
export const quickAddToCart = (context, item, target) => {
  // Remove previous popup selection if exists
  if (prevSelectedPopUp) {
    prevSelectedPopUp.style.display = 'none';
  }

  const selectedId = item?.variant_id || item?.productId;

  if (!selectedId) return;

  // Open current popup selection
  const productItem = target?.closest('.boost-sd__product-item');

  const popupSelection = productItem.querySelector('.boost-sd__popup-select-option');
  if (popupSelection) {
    popupSelection.style.display = 'block';
    handleQuickAddToCartSelectOption(context, popupSelection);
    window.focusedElementBeforeMiniCart = document.activeElement;

    /**
     * Popup Selection - Support WCAG
     */

    focusFirstElement(popupSelection, 'label, button');

    const handlePopupTrapFocus = (e) => {
      trapFocus(e, popupSelection, 'label, button', '.boost-sd__popup-select-option-close-btn');
    };

    popupSelection.addEventListener('keydown', handlePopupTrapFocus);

    prevSelectedPopUp = popupSelection;
  }
};

/**
 * @type {import('./index.d').closeQuickAddToCart}
 */
export const closeQuickAddToCart = (target) => {
  const popupSelection = target.closest('.boost-sd__popup-select-option');
  popupSelection.style.display = 'none';
  target
    .closest('.boost-sd__product-item')
    .querySelector('.boost-sd__button--select-option')
    .focus();
};

/**
 * @type {import('./index.d').handleQuickAddToCartSelectOption}
 */
export const handleQuickAddToCartSelectOption = (context, popupSelection) => {
  const productSwatches = popupSelection.querySelectorAll('.boost-sd__product-swatch');
  const addToCartButton = popupSelection.querySelector('.boost-sd__btn-add-to-cart');
  const addToCartButtonText = popupSelection.querySelector(
    '.boost-sd__btn-add-to-cart .boost-sd__button-text'
  );

  const productItem = popupSelection.closest('.boost-sd__product-item');
  const { variants } = safeParseJSON(productItem.getAttribute('data-product')) || {};

  let productVariants = variants;

  if (typeof variants === 'string') {
    productVariants = safeParseJSON(variants);
  }

  if (!productVariants) return;

  const [getVariant, setVariant] = context.useContextState('selection-option', {});

  setVariant({
    selectedVariant: productVariants[0],
  });

  const resetSelectedOptions = () => {
    const selectedSwatchlabel = popupSelection.querySelectorAll('.boost-sd__radio-label');

    selectedSwatchlabel?.forEach((label) => {
      label?.classList.remove('boost-sd__radio-label--selected');
    });
  };

  resetSelectedOptions();

  const initialSelectedOption = (option) => {
    const label = option?.querySelector('.boost-sd__radio-label');
    if (!label) return;

    const selectedVariant = getVariant()?.selectedVariant;
    if (!selectedVariant || !selectedVariant.title) return;

    const selectedVariantTitle = selectedVariant.title;
    const optionTitle = label.textContent.trim();

    const selectedVariantOptions = [];
    let remainingTitle = selectedVariantTitle;

    // Go through each option in the UI and match it against selectedVariantTitle
    popupSelection.querySelectorAll('.boost-sd__radio-label').forEach((el) => {
      const text = el.textContent.trim();
      if (remainingTitle.startsWith(text)) {
        selectedVariantOptions.push(text);
        remainingTitle = remainingTitle
          .replace(text, '')
          .trim()
          .replace(/^\/\s*/, ''); // Remove leading "/ "
      }
    });

    // Identify the current swatch's index
    const parentSwatch = option.closest('.boost-sd__product-swatch');
    const allSwatches = [...popupSelection.querySelectorAll('.boost-sd__product-swatch')];
    const index = allSwatches.indexOf(parentSwatch);

    // Compare the extracted options correctly
    if (
      index !== -1 &&
      selectedVariantOptions[index]?.toLowerCase() === optionTitle.toLowerCase()
    ) {
      label.classList.add('boost-sd__radio-label--selected');
    }
  };

  const handleSelect = (option) => {
    const swatchOptions = option.parentNode.parentNode.querySelectorAll(
      '.boost-sd__product-swatch-option'
    );
    swatchOptions?.forEach((option) => {
      const label = option.querySelector('.boost-sd__radio-label');
      label?.classList.remove('boost-sd__radio-label--selected');
    });

    const label = option.querySelector('.boost-sd__radio-label');
    label?.classList.add('boost-sd__radio-label--selected');

    const selectedSwatchOptions = popupSelection.querySelectorAll(
      '.boost-sd__radio-label--selected'
    );

    let title = '';
    selectedSwatchOptions?.forEach((option, i) => {
      title += option.textContent.trim();

      if (i < selectedSwatchOptions.length - 1) {
        title += ' / ';
      }
    });

    const selectedVariant = productVariants?.find((variant) => {
      return variant.title === title;
    }) || {
      available: false,
      id: '',
      price: null,
    };

    setVariant({
      selectedVariant,
    });
  };

  productSwatches?.forEach((swatch) => {
    const swatchOptions = swatch.querySelectorAll('.boost-sd__product-swatch-option');
    swatchOptions?.forEach((option) => {
      initialSelectedOption(option);

      option.addEventListener('click', () => {
        handleSelect(option);
      });

      // Support WCAG when selected option using keyboard
      const handleOptionKeyup = (e) => {
        if (KEYS.includes(e.key)) {
          handleSelect(option);
        }
      };

      option.addEventListener('keyup', handleOptionKeyup);
    });
  });

  addToCartButton?.addEventListener('click', async () => {
    const selectedVariant = getVariant().selectedVariant;
    const cartParams = handlePreorderAddToCart(selectedVariant);

    context.handler(addToCart, 'addToCart')(context, cartParams, addToCartButton, false, () => {
      popupSelection && (popupSelection.style.display = 'none');
    });
  });

  context.render(() => {
    const selectedVariant = getVariant().selectedVariant;
    if (!addToCartButton || !selectedVariant) return;

    const isSoldOutVariantSelected = !selectedVariant.available;

    // Handle pre-order button text logic
    handlePreOrderButtonState(addToCartButton, addToCartButtonText, selectedVariant);

    // Keep the existing logic for in-stock/sold-out states
    if (!isPreOrderVariant(selectedVariant)) {
      if (isSoldOutVariantSelected) {
        addToCartButton.disabled = true;

        if (
          addToCartButtonText &&
          !addToCartButtonText.classList.contains('boost-sd__button--icon')
        ) {
          addToCartButtonText.textContent =
            context.app?.translation?.productItem?.soldoutLabel || 'Sold out';
        }
      } else {
        addToCartButton?.removeAttribute('disabled');

        if (
          addToCartButtonText &&
          !addToCartButtonText.classList.contains('boost-sd__button--icon')
        ) {
          addToCartButtonText.textContent =
            context.app?.translation?.productItem?.atcAvailableLabel || 'Add to cart';
        }
      }
    }

    // Set variant ID for analytics tracking
    addToCartButton?.setAttribute('data-variant-id', selectedVariant.id);
  }, ['selection-option']);
};

/**
 * @type {import('./index.d').handleClickOutsideMiniPopup}
 */
export const handleClickOutsideMiniPopup = (target) => {
  // Close quick cart popup selection when click out side
  if (target.closest('.boost-sd__button--select-option')) {
    return;
  }
  const miniPopup = target
    ?.closest('.boost-sd__product-item')
    ?.querySelector('.boost-sd__popup-select-option');
  const miniPopupOpening = document.querySelector(
    '.boost-sd__popup-select-option[style="display: block;"]'
  );
  if (!miniPopup && miniPopupOpening) {
    miniPopupOpening.style.display = 'none';
  }
};

/**
 * @type {import('./index.d').handleClickProductItem}
 */
export const handleClickProductItem = (context, action, target) => {
  // check if click banner-link then open banner link
  if (target?.closest('.boost-sd__product-banner-link')) {
    const bannerElement = target?.closest('.boost-sd__product-banner-link');
    const url = bannerElement.getAttribute('href');
    const openNewTab = bannerElement.getAttribute('target') === '_blank';

    if (openNewTab) {
      window.open(url, '_blank').focus();
    } else {
      setWindowLocation(url);
    }

    return;
  }
  //check click on embedded bundle product list
  if (target?.closest('.boost-sd__product-item--embedded-bundle')) {
    return;
  }

  //check click on volume bundle product list
  if (target?.closest('.boost-sd__product-item--volume-bundle')) {
    return;
  }

  //Check if need to redirect or not, exclude if the tagName is wishlist-button-collection
  const tagName = target?.tagName?.toLowerCase();
  const closestTagWishlist = target?.closest('wishlist-button-collection');
  const noRedirect = target?.dataset?.noRedirect;
  const closestNoRedirect = target?.closest("[data-no-redirect='true']");
  if (
    tagName === 'wishlist-button-collection' ||
    closestTagWishlist ||
    noRedirect === 'true' ||
    closestNoRedirect
  )
    return;

  const productItem = target.closest('.boost-sd__product-item');
  const { addCollectionToProductUrl, current_tags = [] } = context.app?.generalSettings || {};

  if (productItem) {
    const { handle, splitProduct, variantId, variants } =
      safeParseJSON(productItem.getAttribute('data-product')) || {};
    const productId = productItem.getAttribute('data-product-id');

    const productUrl = buildProductDetailUrlWithVariant(
      context,
      { variants, variant_id: variantId, handle, split_product: splitProduct },
      addCollectionToProductUrl,
      current_tags
    );

    const index = getIndexOfProductItem(context, productItem);

    trackSelectedProductPage(context, index, productId);
    setWindowLocation(productUrl);
  }
};

/**
 * @type {import('./index.d').handlePrice}
 */
const handlePrice = ({ priceMin, compareAtPriceMin }) => {
  priceMin = Number(priceMin);
  compareAtPriceMin = Number(compareAtPriceMin);

  const isSale = (compareAtPriceMin || 0) > priceMin;

  const salePercent =
    isSale && compareAtPriceMin
      ? Math.round((((compareAtPriceMin || 0) - priceMin) * 100) / compareAtPriceMin) + '%'
      : undefined;

  const saleAmount = isSale ? (compareAtPriceMin || 0) - priceMin : undefined;

  return {
    isSale,
    salePercent,
    saleAmount,
  };
};

/**
 * @type {import('./index.d').mainPrice}
 */
const mainPrice = async (
  context,
  priceMin,
  compareAtPriceMin,
  priceMax,
  {
    showCentAsSuperscript,
    showCurrencyCodes,
    compareAtPricePosition,
    showSavingDisplay,
    textAlign,
    productItemSavingAmountTranslation,
    productItemAmountTranslation,
  }
) => {
  let html = '';

  const { isSale, salePercent, saleAmount } = handlePrice({
    priceMin,
    compareAtPriceMin,
  });

  const minPriceWithFormat = formatCurrency({
    context,
    value: Number(priceMin),
    showCurrencyCodes,
    showCentAsSuperscript,
    removeDecimalPoint: showCentAsSuperscript,
  });
  const maxPriceWithFormat = formatCurrency({
    context,
    value: Number(priceMax),
    showCurrencyCodes,
    showCentAsSuperscript,
    removeDecimalPoint: showCentAsSuperscript,
  });

  const saleAmountWithFormat = formatCurrency({ context, value: saleAmount, showCurrencyCodes });

  const compareAtPriceWithFormat =
    !compareAtPriceMin || compareAtPriceMin === '0' || !isSale
      ? null
      : formatCurrency({
          context,
          value: Number(compareAtPriceMin),
          showCurrencyCodes,
          showCentAsSuperscript,
          removeDecimalPoint: showCentAsSuperscript,
        });

  const savingPriceWithFormat =
    !showSavingDisplay || !saleAmount
      ? null
      : translateWithComponent(productItemSavingAmountTranslation, {
          salePercent,
          saleAmount: saleAmountWithFormat,
        });

  // priceMax used for multi variant price display where it can have min-max price with format and translation
  const priceValueWithFormat = priceMax
    ? translateWithComponent(productItemAmountTranslation, {
        minPrice: minPriceWithFormat,
        maxPrice: maxPriceWithFormat,
      })
    : minPriceWithFormat;

  /**
   * Corresponding classes for price display matched with Theme Settings
   *   .boost-sd__product-price--default
   *   .boost-sd__product-price--sale
   *   .boost-sd__product-price--compare
   *   .boost-sd__product-price--saving
   */

  html += await context.templateRender(getTemplate(context).productPriceTemplate, {
    compareAtPricePosition,
    isSale,
    textAlign,

    priceValueWithFormat,
    compareAtPriceWithFormat,
    savingPriceWithFormat,
  });

  return html;
};

/**
 * @type {import('./index.d').transformProductPrice}
 */
export const transformProductPrice = (context) => {
  if (context.blockType === 'filter') {
    const productList = context.document?.querySelector('.boost-sd__product-list');
    if (productList?.dataset?.productPrice === 'true') {
      return;
    }
  }

  if (context.blockType === 'recommendation') {
    const recommendation = context.document?.querySelector('.boost-sd__recommendation');
    if (recommendation?.dataset?.productPrice === 'true') {
      return;
    }
  }

  const productItems = context.document?.querySelectorAll(
    '.boost-sd__product-item, .boost-sd__product-item-list-view-layout'
  );

  const { showCentAsSuperscript, showCurrencyCodes, compareAtPricePosition, showSavingDisplay } =
    context?.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};
  const { textAlign } = context?.templateSettings?.themeSettings?.productItems?.productInfo || {};

  // In SSR, field showMultiVariantPrice is removed from themeSettings so we need to check multi variant based on translation
  const productItemAmountTranslation = context.app.translation?.productItem?.amount;
  const productItemSavingAmountTranslation = context.app.translation?.productItem?.savingAmount;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(async (entry) => {
      if (entry.isIntersecting) {
        const { target: productItem } = entry;
        const productPrice = productItem.querySelector('.boost-sd__product-price');

        if (!productPrice) return;

        const { priceMin, priceMax, compareAtPriceMin, compareAtPriceMax } =
          safeParseJSON(productItem.getAttribute('data-product')) || {};
        /** Check if min-max is same value, show one price */
        const isSamePrice =
          `${priceMin}-${compareAtPriceMin}` === `${priceMax}-${compareAtPriceMax}`;

        const regex = /\{\{(?:\s*minPrice\s*|\bminPrice\b)\}}|{{\s*maxPrice\s*|\bmaxPrice\b}}/i;
        const hasMultiVariantPrice = regex?.test(productItemAmountTranslation);

        const noPriceMax = isSamePrice || !hasMultiVariantPrice;

        const priceHTML = await mainPrice(
          context,
          priceMin,
          compareAtPriceMin,
          noPriceMax ? null : priceMax,
          {
            showCentAsSuperscript,
            showCurrencyCodes,
            compareAtPricePosition,
            showSavingDisplay,
            textAlign,
            productItemSavingAmountTranslation,
            productItemAmountTranslation,
          }
        );

        // Has price after transformation
        if (priceHTML && priceHTML.match(/\d/)) {
          productPrice.innerHTML = priceHTML;
        }

        observer.unobserve(productItem);
      }
    });
  });

  productItems?.forEach((productItem) => {
    observer.observe(productItem);
  });
};

/**
 * @type {import('./index.d').trackSelectedProductPage}
 */
const trackSelectedProductPage = (context, index, productId) => {
  const pageParam = getQueryParamByKey('page');
  const currPage = pageParam ? Number(pageParam) : 1;
  const limit = context?.defaultParams?.limit || 24;
  const page = Math.ceil((index + 1) / limit) || 1;
  const prevPage = getSessionStorage(PREV_PAGE);
  const paginationType =
    getThemeSettings(context)?.additionalElements?.pagination?.paginationType || DEFAULT;
  const calc = {
    [DEFAULT]: currPage,
    [LOAD_MORE]: prevPage - 1 + page,
    [INFINITE]: prevPage - 1 + page,
  };

  if (!paginationType) return;

  const actualPage = calc[paginationType];
  const currentCollection = getCurrentCollection();

  setSessionStorage(SELECTED_PRODUCT_PAGE, calc[paginationType]);
  setSessionStorage(SELECTED_PRODUCT_ID, productId);
  setSessionStorage(COLLECTION_SELECTED, currentCollection);

  const { isShortenUrlParam } = getFilterSettings(context);

  setQueryParamHistory('page', actualPage.toString(), true, {
    isShortenUrlParam,
    urlScheme: getUrlScheme(),
  });
};

/**
 * @type {import('./index.d').getIndexOfProductItem}
 */
const getIndexOfProductItem = (context, targetItem) => {
  const productItems = context.document.querySelectorAll(
    '.boost-sd__product-list .boost-sd__product-item'
  );

  for (let i = 0; i < productItems.length; i++) {
    if (productItems[i] === targetItem) {
      return i;
    }
  }
  return -1;
};

/**
 * @type {import('./index.d').setUniformProductImageHeight}
 */
export const setUniformProductImageHeight = (
  context,
  mobileItemsPerRow,
  desktopItemsPerRow,
  mobileBreakPoint,
  tabletPortraitBreakPoint,
  tabletPortraitItemsPerRow
) => {
  /**
   *   The code appears to adjust the aspect ratio of product image wrappers based on the natural dimensions of the images. It does this by:
   *   1. Querying the product image wrappers and items.
   *   2. Determining the number of items per row based on the screen width and theme settings.
   *   3. Iterating through the product items in chunks of numberProductItemPerRow and setting the aspect ratio of each wrapper based on the maximum ratio found in that chunk.
   *   The code also handles cases where images may not have loaded yet by using the onload and onerror events.
   */

  const adjustProductImageHeights = () => {
    const productImageWrappers = context.document.querySelectorAll(
      '.boost-sd__product-image-wrapper--auto'
    );

    if (productImageWrappers.length === 0) {
      return;
    }

    const productItems = Array.from(context.document.querySelectorAll('.boost-sd__product-item'));

    let numberProductItemPerRow = mobileItemsPerRow;

    if (window.innerWidth > tabletPortraitBreakPoint) {
      numberProductItemPerRow = desktopItemsPerRow;
    } else if (window.innerWidth > mobileBreakPoint) {
      numberProductItemPerRow = tabletPortraitItemsPerRow;
    }

    const setAspectRatioForRow = (itemsInRow) => {
      let maxRatio = 0;

      const updateDimensions = (img) => {
        const { naturalHeight, naturalWidth } = img;

        const ratio = naturalHeight / naturalWidth;
        if (ratio > maxRatio) {
          maxRatio = ratio;
        }

        itemsInRow.forEach((item) => {
          const wrapper = item.querySelector('.boost-sd__product-image-wrapper--auto');
          if (wrapper) {
            wrapper.style.aspectRatio = `1/${maxRatio}`;
          }
        });
      };

      itemsInRow.forEach((item) => {
        const img = item.querySelector('.boost-sd__product-image-wrapper--auto img');
        if (img) {
          if (img.complete) {
            updateDimensions(img);
          } else {
            img.onload = () => updateDimensions(img);
            img.onerror = () => {
              console.error('Failed to load image:', img.src);
            };
          }
        }
      });
    };

    for (let i = 0; i < productItems.length; i += numberProductItemPerRow) {
      const itemsInRow = productItems.slice(i, i + numberProductItemPerRow);
      setAspectRatioForRow(itemsInRow);
    }
  };

  // Adjust heights initially and on resize with debounce
  const debouncedAdjustProductImageHeights = debounce(adjustProductImageHeights, 200);

  window.addEventListener('resize', debouncedAdjustProductImageHeights);

  // Run the function once the DOM is fully loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', adjustProductImageHeights);
  } else {
    adjustProductImageHeights();
  }
};

/**
 * @type {import('./index.d').styleBottomCTALayout8}
 */
export const styleBottomCTALayout8 = (context) => {
  const productItems = document.querySelectorAll('.boost-sd__product-item--sublayout-8-2');

  productItems.forEach((item) => {
    const ctaWrapper = item.querySelector('.boost-sd__cta-wrapper');

    item.addEventListener('mouseenter', () => {
      if (ctaWrapper) {
        const ctaHeight = ctaWrapper.offsetHeight;
        ctaWrapper.style.bottom = `-${ctaHeight - 15}px`;
      }
    });

    item.addEventListener('mouseleave', () => {
      if (ctaWrapper) {
        ctaWrapper.style.bottom = '';
      }
    });
  });
};

/**
 * @type {import('./index.d').buildProductDetailUrlWithLocale}
 */
export const buildProductDetailUrlWithLocale = (context) => {
  const productItems = context.document?.querySelectorAll(
    '.boost-sd__product-item, .boost-sd__product-item-list-view-layout'
  );

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const { target: productItem } = entry;
        if (!productItem) return;
        const { handle } = safeParseJSON(productItem.getAttribute('data-product')) || {};
        if (!handle) return;
        const productLinks = productItem.querySelectorAll('.boost-sd__product-link');
        productLinks?.forEach((productLink) => {
          let localeURLPart = '';
          localeURLPart = (window?.Shopify?.routes?.root || '/')?.replace(/\/$/, '');

          productLink.setAttribute('href', `${localeURLPart}/products/${handle}`);
          observer.unobserve(productLink);
        });
      }
    });
  });

  productItems?.forEach((productItem) => {
    observer.observe(productItem);
  });
};
